import { stripLocalePrefix } from '~/i18n/i18n-config';
import { logError } from './logger';

export function getTrackingParamsFromCookies(cookies?: Record<string, string | undefined>) {
  let campaign = '';
  let campaignId = '';
  let source = '';
  let term = '';
  let medium = '';
  let gclid = '';
  let papCookie = ''; // post affiliate pro cookie

  if (!cookies) {
    return {
      campaign,
      campaignId,
      source,
      term,
      medium,
      gclid,
      papCookie
    };
  }

  const campaignUrl = cookies.__gtm_campaign_url;

  if (campaignUrl && typeof campaignUrl === 'string') {
    try {
      const parsedUrl = new URL(campaignUrl);
      const searchParams = parsedUrl.searchParams;

      campaign = searchParams.get('utm_campaign') ?? '';
      campaignId = searchParams.get('campaignid') ?? '';
      source = searchParams.get('utm_source') ?? '';
      term = searchParams.get('utm_term') ?? '';
      medium = searchParams.get('utm_medium') ?? '';
      gclid = searchParams.get('gclid') ?? '';
    } catch {
      logError(new Error(`getTrackingParamsFromCookies: invalid campaign URL: ${campaignUrl}`));
    }
  }

  // post affiliate pro campaign name
  if (cookies.PAPAffiliateId && typeof cookies.PAPAffiliateId === 'string') {
    campaign = cookies.PAPAffiliateId;
  }

  if (cookies.papCookie && typeof cookies.papCookie === 'string') {
    papCookie = cookies.papCookie;
  }

  return {
    campaign,
    campaignId,
    source,
    term,
    medium,
    gclid,
    papCookie
  };
}

// for tracking - to determine original page from pathname
export const getTrackingCategory = (pathname: string | null) => {
  if (!pathname) return 'unknown';

  // Strip the [lang] prefix so migrated routes (/en-US/destination/…) classify
  // the same as their bare (default)-tree equivalents.
  const basePath = stripLocalePrefix(pathname);

  if (basePath === '/') return 'homepage';
  if (basePath.startsWith('/blueprint-index')) return 'bpi';
  if (basePath.startsWith('/map/build')) return 'playground_build';
  if (basePath.startsWith('/map')) return 'playground';
  if (basePath.startsWith('/journey')) return 'trip_page';
  if (basePath.startsWith('/destination') || basePath.startsWith('/inspiration')) return 'marketing';
  return 'unknown';
};

/**
 * Reports whether an imperative navigation carried the active locale into the pushed href, so the
 * card-click funnels can be segmented by it. `has_lang_segment` is false for `en-GB` by design —
 * `localePrefix: 'as-needed'` serves the default locale unprefixed, so the expectation is
 * per-locale and evaluated in the analytics query, not here.
 */
export function getLocalePrefixTrackingParams(href: string, locale: string) {
  const [firstSegment] = href.split(/[?#]/)[0].split('/').filter(Boolean);

  return {
    locale,
    has_lang_segment: firstSegment === locale
  };
}

// Safely extract GTM tracking params from browser cookie (client-side only)
export function getUtmParamsFromTrackingCookie() {
  const defaultParams = { source: '', campaign: '', medium: '' };

  try {
    if (typeof document === 'undefined') {
      return defaultParams;
    }

    const rawCookie = /(?:^|; )__gtm_campaign_url=([^;]*)/.exec(document.cookie)?.[1];
    if (!rawCookie) {
      return defaultParams;
    }

    const result = getTrackingParamsFromCookies({ __gtm_campaign_url: decodeURIComponent(rawCookie) });
    return {
      source: result.source,
      campaign: result.campaign,
      medium: result.medium
    };
  } catch {
    logError(new Error('Failed to extract GTM params from browser cookie'));
    return defaultParams;
  }
}
